import { useEffect, useRef, useState } from 'preact/hooks';

import { dateLabel, t } from '../i18n';
import { Icon } from './icons';
import { StatusBadge, TypeLabel } from './MyFeedback';

import type { ApiClient } from '../api';
import type { Pin, Selection } from '../types';

const PIN = 28;
/** How far a group of pins on one spot fans out below it. */
const FAN_DROP = 46;
const FAN_GAP = 42;

/** Where a report pointed, in current viewport coordinates. Elements are found again by selector. */
function locate(anchor: Selection): { x: number; y: number } | null {
  if (anchor.kind === 'element') {
    try {
      const el = document.querySelector(anchor.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width || r.height) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }
    } catch {
      // Invalid selector; fall back to the recorded position.
    }
  }
  const was = anchor.scroll ?? { x: window.scrollX, y: window.scrollY };
  return {
    x: anchor.rect.x + anchor.rect.width / 2 + was.x - window.scrollX,
    y: anchor.rect.y + anchor.rect.height / 2 + was.y - window.scrollY,
  };
}

interface Placed {
  pin: Pin;
  number: number;
  x: number;
  y: number;
  /** Set when the pin fanned out; the line runs from here to the pin. */
  from: { x: number; y: number } | null;
}

/** Pins on the same spot fan out in a row below it, each connected to the spot by a dashed line. */
function place(pins: Pin[]): Placed[] {
  const groups = new Map<
    string,
    Array<{ pin: Pin; number: number; at: { x: number; y: number } }>
  >();
  pins.forEach((pin, i) => {
    const at = locate(pin.anchor);
    if (!at) return;
    const key = `${Math.round(at.x / 12)}:${Math.round(at.y / 12)}`;
    groups.set(key, [...(groups.get(key) ?? []), { pin, number: i + 1, at }]);
  });
  return [...groups.values()].flatMap((group) =>
    group.map(({ pin, number, at }, i) =>
      group.length === 1
        ? { pin, number, x: at.x, y: at.y, from: null }
        : {
            pin,
            number,
            x: at.x + (i - (group.length - 1) / 2) * FAN_GAP,
            // Fan out below the spot, or above it when the spot is near the bottom of the screen.
            y: at.y + (at.y + FAN_DROP > window.innerHeight - PIN ? -FAN_DROP : FAN_DROP),
            from: at,
          },
    ),
  );
}

/**
 * Markers for what testers already reported on this page, so the next tester sees it is known.
 * Shown while the sidebar is docked open. Own reports open in the sidebar; others show a summary.
 */
export function Pins({
  api,
  email,
  visibleWidth,
  offsetLeft,
  onOpenOwn,
  refreshKey,
}: {
  api: ApiClient;
  email: string | null;
  /** Width of the page area next to the sidebar; pins beyond it are hidden. */
  visibleWidth: number;
  offsetLeft: number;
  onOpenOwn: (id: string) => void;
  /** Changes when a new report was sent, so its pin appears. */
  refreshKey: number;
}) {
  const s = t();
  const [pins, setPins] = useState<Pin[]>([]);
  const [href, setHref] = useState(location.href);
  const [, setFrame] = useState(0);
  const [active, setActive] = useState<string | null>(null);
  const raf = useRef(0);

  // Single-page apps change the URL without reloading; follow along.
  useEffect(() => {
    const timer = setInterval(
      () => setHref((h) => (h === location.href ? h : location.href)),
      1000,
    );
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .pins(href, email)
      .then((p) => !cancelled && setPins(p))
      .catch(() => !cancelled && setPins([]));
    return () => {
      cancelled = true;
    };
  }, [href, email, refreshKey]);

  // Re-measure on scroll, resize and layout changes.
  useEffect(() => {
    const tick = () => {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => setFrame((f) => f + 1));
    };
    window.addEventListener('scroll', tick, true);
    window.addEventListener('resize', tick);
    const observer = new MutationObserver(tick);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setActive(null);
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('scroll', tick, true);
      window.removeEventListener('resize', tick);
      window.removeEventListener('keydown', onKey);
      observer.disconnect();
    };
  }, []);

  const right = offsetLeft + visibleWidth;
  const visible = place(pins).filter(
    (p) =>
      p.x > offsetLeft && p.x < right - PIN / 2 && p.y > 0 && p.y < window.innerHeight - PIN / 2,
  );
  const tone = (pin: Pin) =>
    pin.status.category === 'done' ? 'closed' : pin.mine ? 'mine' : 'other';

  return (
    <div class="hfb-pins" style={{ left: offsetLeft, width: visibleWidth }}>
      <svg class="hfb-pin-lines" aria-hidden="true">
        {visible
          .filter((p) => p.from)
          .map((p) => (
            <line
              key={p.pin.id}
              x1={p.from!.x - offsetLeft}
              y1={p.from!.y}
              x2={p.x - offsetLeft}
              y2={p.y}
              stroke-dasharray="3 4"
            />
          ))}
        {[...new Set(visible.filter((p) => p.from).map((p) => `${p.from!.x}:${p.from!.y}`))].map(
          (key) => {
            const [x, y] = key.split(':').map(Number);
            return <circle key={key} cx={x! - offsetLeft} cy={y} r="4" />;
          },
        )}
      </svg>

      {visible.map((p) => {
        const open = active === p.pin.id;
        const x = p.x - offsetLeft;
        const flipX = p.x > right - 320;
        const flipY = p.y > window.innerHeight - 240;
        return (
          <div
            key={p.pin.id}
            class="hfb-pin-wrap"
            style={{ left: x - PIN / 2, top: p.y - PIN / 2 }}
          >
            <button
              type="button"
              class={`hfb-pin is-${tone(p.pin)}${open ? ' is-open' : ''}`}
              aria-expanded={open}
              aria-label={`${p.number}. ${p.pin.title}`}
              onClick={() => setActive(open ? null : p.pin.id)}
            >
              {p.number}
            </button>
            {open && (
              <div
                class={`hfb-pin-card${flipX ? ' is-left' : ''}${flipY ? ' is-up' : ''}`}
                role="dialog"
                aria-label={p.pin.title}
              >
                <header>
                  <TypeLabel type={p.pin.type} />
                  <StatusBadge status={p.pin.status} />
                  <button
                    type="button"
                    class="hfb-icon-btn"
                    aria-label={s.close}
                    onClick={() => setActive(null)}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </header>
                <strong>{p.pin.title}</strong>
                <span class="hfb-muted">
                  {p.pin.mine ? s.you : p.pin.reporterName} · {dateLabel(p.pin.createdAt)}
                </span>
                <footer>
                  {p.pin.mine ? (
                    <button
                      type="button"
                      class="hfb-btn hfb-btn-primary"
                      onClick={() => {
                        setActive(null);
                        onOpenOwn(p.pin.id);
                      }}
                    >
                      {s.openConversation}
                    </button>
                  ) : (
                    <span>{s.alreadyReported}</span>
                  )}
                </footer>
              </div>
            )}
          </div>
        );
      })}

      {pins.length > 0 && (
        <div class="hfb-pin-legend">
          <strong>{s.pinsOnPage(pins.length)}</strong>
          <span>
            <i class="is-mine" /> {s.yours}
          </span>
          <span>
            <i class="is-other" /> {s.others}
          </span>
          <span>
            <i class="is-closed" /> {s.closed}
          </span>
        </div>
      )}
    </div>
  );
}
